import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {forkJoin, Subject} from 'rxjs';
import {Dispatch, Program, Site, UIDispatch, ZonedDispatch} from '../../../../domain/cloud-topology';
import {DispatchService} from '../../../../services/vpp/dispatch.service';
import {DispatchNewComponent} from '../dispatch-new/dispatch-new.component';
import {takeUntil} from 'rxjs/operators';

import {faPencilAlt, faTrash} from '@fortawesome/free-solid-svg-icons';
import {ProgramService} from '../../../../services/vpp/program.service';
import moment from 'moment-timezone';
import {SiteService} from '../../../../services/site/site.service';
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {ResourceService} from "../../../../services/resource.service";
import {
  ACTUAL_POWER_CRITICAL, ACTUAL_POWER_UNDERPERFORMING, BAD_DER_STATE, BAD_SITE_STATE,
  cancelStatus, CHARGING_NOT_GENERATING, DEFAULT_UNIT, DISPATCH_ACTIVE, DISPATCH_ERROR, DISPATCH_PENDING,
  errorStatus, GENERATING_NOT_CHARGING, NOT_AUTOMATIC_GENERATION, PLANNED_POWER_CRITICAL, PLANNED_POWER_UNDERPERFORMING,
  saveStatus, UNKNOWN_ACTIVITY_STATUS, UNKNOWN_SITE_DISPATCH_STATUS
} from "../../../../domain/constants";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {SelectionModel} from "@angular/cdk/collections";

@Component({
    selector: 'app-dispatch-listings',
    templateUrl: './dispatch-listings.component.html',
    styleUrls: ['dispatch-listings.component.scss'],
    standalone: false
})
export class DispatchListingsComponent implements OnChanges, OnDestroy {
  @Input() program: Program;
  @Input() displayHeader: boolean = true;
  @Input() includeTypes: string[];
  @Input() excludeTypes: string[];
  @Input() dispatchHourTimeWindow: number;
  @Output() newDispatchEmitter = new EventEmitter<Dispatch>();
  @Output() selectedDispatchEmitter: EventEmitter<ZonedDispatch> = new EventEmitter<ZonedDispatch>();

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  expandedElement: Dispatch | null;

  private programSites: Site[];
  timezone: string;
  private subject$: Subject<void>;
  dispatches: ZonedDispatch[];
  faTrash = faTrash;
  faPencil = faPencilAlt;
  requestsCompleted: boolean = false;
  expanded: boolean = false;
  page = 1;
  pageSize = 10;
  dateFormatStr: string = 'MM/dd/yyyy HH:mm';
  displayedColumns: string[] = ['status', 'setpoint', 'requestedPower', 'predictedOutput', 'maxDispatchPower', 'incentive', 'penalty', 'startTimeUtc', 'endTimeUtc'];
  dataSource: MatTableDataSource<Dispatch>;
  selection = new SelectionModel<Dispatch>(false, null);
  readonly dialog = inject(MatDialog)

  constructor(private dispatchService: DispatchService,
              private programService: ProgramService,
              private siteService: SiteService,
              private snackBar: MatSnackBar) {
      this.dataSource = new MatTableDataSource<Dispatch>();
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['program']['currentValue'] !== undefined) {
      this.program = changes['program']['currentValue'];
      if (this.displayHeader && !this.displayedColumns.includes('actions')) {
        this.displayedColumns.push('actions')
      }
      this.subject$ = new Subject<void>();
      this.getDispatches();
    }
  }

  isHistorical(): boolean {
    if (this.includeTypes === undefined) {
      return false;
    }
    return this.includeTypes.join('') === ['COMPLETE', 'ERROR', 'CANCELED'].join('');
  }

  getDispatches(): void {
    if (this.program) {
      this.dispatches = [];
      this.requestsCompleted = false;
      // tslint:disable-next-line:max-line-length
      this.programService.getProgramSites(this.program).pipe(takeUntil(this.subject$)).subscribe(sites => {
        this.programSites = sites;
        if (sites.length > 0) {
          this.siteService.getSite(sites[0].id, true).pipe(takeUntil(this.subject$)).subscribe(site => {
            if (!site.timezone) {
              this.snackBar.open("Attention: Timezone is not set. Defaulting to UTC.", "Close", ResourceService.getDefaultSnackBarConfig());
              this.timezone = 'UTC';
            } else {
              this.timezone = site.timezone;
            }
            this.getProgramDispatches();
          });
        } else {
          this.timezone = 'UTC';
          this.snackBar.open("Attention: Timezone is not set. Defaulting to UTC.", "Close", ResourceService.getDefaultSnackBarConfig());
          this.getProgramDispatches();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  private getProgramDispatches() {
    this.dispatchService.getProgramDispatches(this.program, this.timezone, this.includeTypes, this.excludeTypes).pipe(takeUntil(this.subject$)).subscribe(dispatches => {
      const currDate = moment().tz(this.timezone);
      this.dispatches = this.filterDispatches(dispatches as ZonedDispatch[]);

      for (const dispatch of this.dispatches) {
        dispatch.predictedOutput = 0 + ' kW';
        dispatch.shortZone = moment(dispatch.startTimeUtc).tz(this.timezone, true).zoneAbbr();
        if (this.programSites.length != 0) {
          dispatch.sites = this.programSites;
        } else {
          dispatch.sites = [];
        }

        forkJoin([
          this.dispatchService.getDispatchStatus(dispatch),
        ]).pipe(takeUntil(this.subject$)).subscribe(asyncResults => {
          dispatch.dispatchStats = asyncResults[0];
          const duration = moment.duration(moment(dispatch.startTimeUtc).diff(currDate));
          if (duration.asHours() > 24) {
            dispatch.predictedOutput = 'Insufficient Data';
          } else {
            dispatch.predictedOutput = (dispatch.dispatchStats.plannedPower) ? +dispatch.dispatchStats.plannedPower.value.toFixed(1).replace(',', '') + ' kW' : 0 + ' kW';
          }
          dispatch.maxDispatchPower = (dispatch.dispatchStats.maxDispatchPower.value) ? +UIDispatch.cleanNumericOutput(dispatch.dispatchStats.maxDispatchPower.value) : 0;
          dispatch.requestedPower = (dispatch.dispatchStats.requestedPower.value) ? +UIDispatch.cleanNumericOutput(dispatch.dispatchStats.requestedPower.value): 0;
          dispatch.penalty = +UIDispatch.cleanNumericOutput(dispatch.penalty);
        }, (errors) => {
          dispatch.status = 'UNKNOWN';
          dispatch.predictedOutput = 'Insufficient Data';
          dispatch.requestedPower = 0;
          dispatch.penalty = 0;
        });
      }

      this.requestsCompleted = true;
      let closestDispatch;
      this.dispatches.forEach((dispatch) => {
        let diff = moment(dispatch.startTimeUtc).diff(currDate, 'seconds');
        if (diff > 0) {
          if (closestDispatch) {
            if (moment(closestDispatch.startTimeUtc).diff(currDate, 'seconds') < 0) {
              closestDispatch = dispatch;
            }
          }
          else {
            closestDispatch = dispatch;
          }
          this.selectedDispatchEmitter.emit(closestDispatch);
        } else {
          closestDispatch = dispatch;
          this.selectedDispatchEmitter.emit(closestDispatch);
        }
      });
      this.dataSource = new MatTableDataSource<Dispatch>(this.dispatches);
      this.selection.select(this.dispatches[0]);
      setTimeout(() => {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
      });
    });
  }


  private filterDispatches(dispatches: ZonedDispatch[]): ZonedDispatch[] {
    if (!this.dispatchHourTimeWindow) {
       return this.sortByDispatchStatus(dispatches);
    } else {
      let filteredDispatches: ZonedDispatch[] = [];
      for (const dispatch of dispatches) {
        const startTime = moment(dispatch.startTimeUtc);
        const currDate = moment();
        if (Math.abs(currDate.diff(startTime, 'hours')) <= this.dispatchHourTimeWindow) {
          filteredDispatches.push(dispatch);
        }
      }

      return this.sortByDispatchStatus(filteredDispatches);
    }

  }

  private sortByDispatchStatus(dispatches: ZonedDispatch[]): ZonedDispatch[] {
    let statusSortElements = [];
    statusSortElements = statusSortElements.concat(dispatches.filter(dispatch => {
      return dispatch.status == 'ERROR' && moment().utc().diff(moment(dispatch.startTimeUtc).utc(), 'hours') < 24;
    }).sort((a, b) =>
      (moment(b.startTimeUtc).unix() - moment(a.startTimeUtc).unix())));
    statusSortElements = statusSortElements.concat(dispatches.filter(dispatch => { return dispatch.status == 'PENDING'}).sort((a, b) =>
      moment(b.startTimeUtc).unix() - moment(a.startTimeUtc).unix()));
    statusSortElements = statusSortElements.concat(dispatches.filter(dispatch => { return dispatch.status == 'ACTIVE'}).sort((a, b) =>
      moment(b.startTimeUtc).unix() - moment(a.startTimeUtc).unix()));
    statusSortElements = statusSortElements.concat(dispatches.filter(dispatch => { return dispatch.status == 'COMPLETE'}).sort((a, b) =>
      moment(b.startTimeUtc).unix() - moment(a.startTimeUtc).unix()));

    return statusSortElements;
  }

  addNewDispatch(): void {
    if (this.programSites.length) {
     this.editDispatch(null);
    } else {
      this.snackBar.open("Program must have at least one site assigned to create new dispatch.", "Close", {
        duration: 3000,
        horizontalPosition: "right",
        verticalPosition: "bottom"
      });
    }
  }

  editDispatch(dispatchId: string): void {
    let foundDispatch = this.dispatches.find((dispatch) => {
      return dispatchId === dispatch.id;
    });

    let dispatchToEdit;
    if (!foundDispatch) {
      dispatchToEdit = {
        id: null,
        type: this.program.programType,
        setpoint: 0,
        status: DISPATCH_PENDING,
        duration: null,
        incentive: 0,
        penalty: 0,
      };
    } else {
      dispatchToEdit = foundDispatch;
    }
    if (dispatchToEdit.status == DISPATCH_PENDING || dispatchToEdit.status == DISPATCH_ERROR) {
      let dialogRef = this.dialog.open(DispatchNewComponent, {
        width: '1100px',
        data: {
          dispatch: dispatchToEdit,
          program: this.program,
          timezone: this.timezone
        },
      });

      dialogRef.afterClosed().subscribe(data => {
        if (data['status'] !== cancelStatus) {
          const result = data['dispatch'];
          const dateStr = ResourceService.convertBootstrapDate(result);
          // tslint:disable-next-line:max-line-length
          const endDate = moment(dispatchToEdit.startTimeUtc).add(result['duration'], result['durationType'].toLowerCase());
          dispatchToEdit.startTimeUtc = moment.tz(`${dateStr}`, this.timezone).format()
          dispatchToEdit.endTimeUtc = endDate.format();
          dispatchToEdit.duration = `PT${result['duration']}${result['durationType']}`;
          dispatchToEdit.setpoint = result['setpoint'];
          dispatchToEdit.incentive = result['incentive'];
          dispatchToEdit.penalty = result['penalty'];
          dispatchToEdit.type = this.program.programType;
          delete dispatchToEdit.sites;
          delete dispatchToEdit.predictedOutput;
          delete dispatchToEdit.dispatchStats;
          delete dispatchToEdit.maxDispatchPower;
          delete dispatchToEdit.requestedPower;
          if (dispatchToEdit.id !== null) {
            this.dispatchService.putProgramDispatch(this.program, dispatchToEdit, this.timezone).pipe(takeUntil(this.subject$))
              .subscribe(updatedDispatch => {
                this.updateDispatchPredictedOutput(updatedDispatch, false);

                this.snackBar.open("Dispatch has been updated.", "Close", {
                  duration: 3000,
                  horizontalPosition: "right",
                  verticalPosition: "bottom"
                });

              }, (err) => {
                this.snackBar.open("There was a problem updating the dispatch.", "Close", ResourceService.getDefaultSnackBarConfig());
              });
          }
          if (data['status'] == errorStatus) {
            this.snackBar.open("You can not add a dispatch to this program", "Close", ResourceService.getDefaultSnackBarConfig());
          }

          if (data['status'] == saveStatus && dispatchToEdit.id == null) {
            this.dispatchService.postProgramDispatch(this.program, dispatchToEdit).pipe(takeUntil(this.subject$)).subscribe(createdDispatch => {
              const dispatch = UIDispatch.convertFromUtc(createdDispatch, this.timezone);
              dispatch['shortZone'] = moment(createdDispatch.startTimeUtc).tz(this.timezone, true).zoneAbbr();
              this.updateDispatchPredictedOutput(dispatch, true);
              this.dataSource.data.unshift(dispatch);
              this.dataSource.data = [...this.dataSource.data];

              this.snackBar.open("Dispatch has been created.", "Close", {
                duration: 3000,
                horizontalPosition: "right",
                verticalPosition: "bottom"
              });
              this.newDispatchEmitter.emit(createdDispatch);
            }, (err) => {
              console.error(`Problem creating dispatch reason: ${err}`);
              this.snackBar.open("There was a problem creating the dispatch", "Close", ResourceService.getDefaultSnackBarConfig());
            });
          }
        } else {
          this.snackBar.open("Changes have been discarded.", "Close", ResourceService.getDefaultSnackBarConfig());
        }
        dialogRef = null;
        foundDispatch = null;
      });
    }

  }

  updateDispatchListings(dispatch: Dispatch) {
    const updatedDispatches = [];
    for (const oldDispatch of this.dispatches) {
      if (oldDispatch.id === dispatch.id) {
        updatedDispatches.push(dispatch);
      } else {
        updatedDispatches.push(oldDispatch);
      }
    }
    this.dispatches = updatedDispatches;
  }

  updateDispatchPredictedOutput(dispatch, newDispatch: boolean): void {
    dispatch.predictedOutput = 0;
    this.dispatchService.getDispatchStatus(dispatch).pipe(takeUntil(this.subject$)).subscribe(data => {
      dispatch.dispatchStats = data;
      dispatch.predictedOutput = UIDispatch.cleanNumericOutput(dispatch.dispatchStats.plannedPower.value) + ' kW';
      dispatch.maxDispatchPower = UIDispatch.cleanNumericOutput(dispatch.dispatchStats.maxDispatchPower.value);
      dispatch.requestedPower = UIDispatch.cleanNumericOutput(dispatch.dispatchStats.requestedPower.value);
      let currDate = moment().tz(this.timezone);
      const duration = moment.duration(moment(dispatch.startTimeUtc).diff(currDate));
      if (duration.asHours() > 24) {
        dispatch.predictedOutput = 'Insufficient Data';
      }
      if (newDispatch) {
        this.dispatches.push(dispatch as ZonedDispatch);
      } else {
        this.updateDispatchListings(dispatch);
      }
    });
  }

  removeDispatch(dispatch) {
    if (!window.confirm('Are you sure you want to remove the dispatch?')) {
      this.snackBar.open("Changes have been discarded.", "Close", ResourceService.getDefaultSnackBarConfig());
      return;
    }
    this.dispatchService.deleteProgramDispatch(this.program, dispatch).pipe(takeUntil(this.subject$)).subscribe( () => {
      const dispatches = [];
      for (const dispatchElement of this.dispatches) {
        if (dispatchElement.id !== dispatch.id) {
          dispatches.push(dispatchElement);
        }
      }
      this.dispatches = dispatches;
      this.dataSource.data = this.dispatches;
      this.snackBar.open("Dispatch has been deleted.", "Close", ResourceService.getDefaultSnackBarConfig());
    }, (err) => {
      console.error(`Problem deleting dispatch reason: ${err}`);
      this.snackBar.open("There was a problem removing the dispatch", "Close", ResourceService.getDefaultSnackBarConfig());
    });
  }

  setSelectedDispatch(element: ZonedDispatch) {
    this.selectedDispatchEmitter.emit(element)
  }

  cleanRequestedPower(element) {
    for (const dispatch of this.dispatches) {
      if (dispatch.dispatchStats && element.id == dispatch.id && dispatch.dispatchStats.requestedPower) {
        return `${UIDispatch.cleanNumericOutput(dispatch.dispatchStats.requestedPower.value)}` + ' kW';
      }
    }
    return `0` + ' kW';
  }

  cleanNumericOutput(value) {
    return  UIDispatch.cleanNumericOutput(value);
  }

  getDispatchStatusDetailAlert(element, keys: string[]) {
    const statusDetails = [
      PLANNED_POWER_CRITICAL,
      PLANNED_POWER_UNDERPERFORMING,
      ACTUAL_POWER_CRITICAL,
      ACTUAL_POWER_UNDERPERFORMING,
      UNKNOWN_SITE_DISPATCH_STATUS,
      UNKNOWN_ACTIVITY_STATUS,
      GENERATING_NOT_CHARGING,
      CHARGING_NOT_GENERATING,
      BAD_SITE_STATE,
      BAD_DER_STATE,
      NOT_AUTOMATIC_GENERATION
    ]

    for (const dispatch of this.dispatches) {
      if (dispatch.dispatchStats && (element.id == dispatch.id)) {
        for (const key of keys) {
          if (statusDetails.includes(key)) {
            return true;
          }
        }
      }
    }

    return false;
  }
  get filteredDispatches(): ZonedDispatch[] {
    if (this.dispatches) {
      return this.dispatches
        .sort((a, b) => {
          return moment(b.startTimeUtc).valueOf() - moment(a.startTimeUtc).valueOf();
        })
        .slice((this.page - 1) * this.pageSize, (this.page - 1) * this.pageSize + this.pageSize);
    }
  }

  removeComma(value) {
    return value.replace(',', '');
  }

  protected readonly DISPATCH_ERROR = DISPATCH_ERROR;
  protected readonly DISPATCH_PENDING = DISPATCH_PENDING;
}
